import type { ChannelConfig } from "./types"

/**
 * Horario de atendimento.
 *
 * Fora do horario o widget AVISA, mas nao bloqueia o envio: a mensagem fica na
 * fila e o atendente responde na abertura. Bloquear o campo transforma "estamos
 * fechados" em "vá embora" — e o fluxo automatico continua funcionando de
 * madrugada de qualquer jeito.
 */
export function isWithinBusinessHours(
  config: ChannelConfig | null,
  now: Date = new Date(),
): boolean {
  const ranges = config?.businessHours?.ranges
  // Sem horario configurado = sempre aberto. Um canal recem-criado nao pode
  // nascer dizendo que esta fechado.
  if (!Array.isArray(ranges) || ranges.length === 0) return true

  const tz = config?.businessHours?.tz
  const minutes = minutesOfDay(now, tz)
  if (minutes === null) return true

  return ranges.some(([from, to]) => {
    const start = parseHhMm(from)
    const end = parseHhMm(to)
    if (start === null || end === null) return false
    // Faixa que cruza a meia-noite (ex.: 22:00–02:00).
    return end >= start
      ? minutes >= start && minutes < end
      : minutes >= start || minutes < end
  })
}

function parseHhMm(value: unknown): number | null {
  if (typeof value !== "string") return null
  const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim())
  if (!match) return null
  const hours = Number(match[1])
  const mins = Number(match[2])
  if (hours > 23 || mins > 59) return null
  return hours * 60 + mins
}

function minutesOfDay(now: Date, timeZone?: string): number | null {
  try {
    const formatter = new Intl.DateTimeFormat("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      ...(timeZone ? { timeZone } : {}),
    })
    const parts = formatter.formatToParts(now)
    const hour = Number(parts.find((p) => p.type === "hour")?.value)
    const minute = Number(parts.find((p) => p.type === "minute")?.value)
    if (!Number.isFinite(hour) || !Number.isFinite(minute)) return null
    // `hour12: false` devolve 24 à meia-noite em alguns runtimes.
    return (hour % 24) * 60 + minute
  } catch {
    // Fuso invalido na config nao pode derrubar o widget.
    return null
  }
}

export function formatTime(unixSeconds: number): string {
  return new Date(unixSeconds * 1000).toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
  })
}
